import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Star, ArrowRight } from 'lucide-react';
import { LOGIN_BANNERS, DEFAULT_BANNER_IMAGE, type LoginBanner } from './loginBanners';
import { cn } from '@/lib/utils';
import { useActiveLoginBanners } from '@/hooks/useLoginBanners';
import { getBannerTag } from './bannerTags';

const INTERVAL = 7000;

export default function LoginBannerSlider() {
  const { data, isError } = useActiveLoginBanners();
  const banners: LoginBanner[] = useMemo(() => {
    if (isError || !data?.length) return LOGIN_BANNERS;
    return data.map((b) => ({
      id: b.id,
      tag: b.tag ?? undefined,
      title: b.title,
      highlight: b.highlight ?? undefined,
      description: b.description,
      image: b.image_url || DEFAULT_BANNER_IMAGE,
      ctaLabel: b.cta_label ?? undefined,
      ctaHref: b.cta_href ?? undefined,
    }));
  }, [data, isError]);
  useEffect(() => { setIndex(0); }, [banners.length]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (dir: number) => setIndex((i) => (i + dir + banners.length) % banners.length),
    [banners.length],
  );

  useEffect(() => {
    if (paused || banners.length < 2) return;
    const t = window.setTimeout(() => go(1), INTERVAL);
    return () => window.clearTimeout(t);
  }, [index, paused, go, banners.length]);

  if (!banners.length) return null;

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Novidades do AutoFluxo"
      className="relative h-full min-h-[520px] overflow-hidden rounded-3xl border border-white/10 bg-black/60 shadow-2xl"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {banners.map((b, i) => (
        <div
          key={b.id}
          aria-hidden={i !== index}
          className={cn(
            'absolute inset-0 transition-opacity duration-700',
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          {b.image && (
            <img
              src={b.image}
              alt=""
              loading={i === 0 ? 'eager' : 'lazy'}
              className="absolute inset-y-0 right-0 h-full w-[68%] object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-transparent" />
          <div className="relative z-10 flex h-full max-w-[60%] flex-col justify-center gap-5 pl-16 pr-4">
            {b.tag && (
              <span className={cn('inline-flex w-fit items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-wide', getBannerTag(b.tag).slider)}>
                <Star className="h-3.5 w-3.5 fill-current" />
                {b.tag}
              </span>
            )}
            <h2 className="text-[2rem] font-bold leading-tight text-white xl:text-[2.6rem]">
              {b.title}
              {b.highlight && <span className="block text-imperial-red">{b.highlight}</span>}
            </h2>
            <p className="text-base leading-relaxed text-white/80 xl:text-lg">{b.description}</p>
            {b.ctaLabel && b.ctaHref && (
              <a
                href={b.ctaHref}
                className="inline-flex w-fit items-center gap-2 rounded-lg border border-imperial-red px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-imperial-red/20"
              >
                {b.ctaLabel} <ArrowRight className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      ))}

      {banners.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Banner anterior"
            onClick={() => go(-1)}
            className="absolute left-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Próximo banner"
            onClick={() => go(1)}
            className="absolute right-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2">
            {banners.map((b, i) => (
              <button
                key={b.id}
                type="button"
                aria-label={`Ir para banner ${i + 1}`}
                aria-current={i === index}
                onClick={() => setIndex(i)}
                className={cn(
                  'h-2.5 rounded-full transition-all',
                  i === index ? 'w-6 bg-imperial-red' : 'w-2.5 bg-white/40 hover:bg-white/60',
                )}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
